import { queryOptions, useQuery } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';

const SECURITY_CONTAINER_IMAGES_QUERY_KEY = 'security-container-images';

export type ExceptionFilter = 'all' | 'active' | 'excepted';

export type ContainerImagesFilter = {
  status?: string;
  cves?: string[];
  packageNames?: string[];
  namespaces?: string[];
  clusterIds?: string[];
  labels?: string[];
  exceptionFilter?: ExceptionFilter;
  resourceIdFilter?: string;
};

type UseContainerImagesQueryArgs = {
  filter?: ContainerImagesFilter;
  enabled?: boolean;
};

export const getContainerImagesQueryKey = (filter?: ContainerImagesFilter) =>
  filter
    ? ([
        SECURITY_CONTAINER_IMAGES_QUERY_KEY,
        filter.status,
        filter.cves,
        filter.packageNames,
        filter.namespaces,
        filter.clusterIds,
        filter.labels,
        filter.exceptionFilter,
        filter.resourceIdFilter,
      ] as const)
    : ([SECURITY_CONTAINER_IMAGES_QUERY_KEY] as const);

const getContainerImagesQueryOptions = ({
  filter = { exceptionFilter: 'active' },
  enabled = true,
}: UseContainerImagesQueryArgs) =>
  queryOptions({
    queryKey: getContainerImagesQueryKey(filter),
    queryFn: async () => {
      const { data } = await apiClient.securityInsights.getContainerImages({
        status: filter.status,
        cves: filter.cves,
        packages: filter.packageNames,
        namespaces: filter.namespaces,
        clusterIds: filter.clusterIds,
        labels: filter.labels,
        exceptionFilter: filter.exceptionFilter,
        resourceIdFilter: filter.resourceIdFilter,
      });

      return data.items || null;
    },
    enabled,
  });

export const useContainerImagesQuery = (
  params: UseContainerImagesQueryArgs = {}
) => {
  const {
    isLoading,
    data: images,
    error,
    refetch,
  } = useQuery(getContainerImagesQueryOptions(params));

  return { isLoading, images: images || undefined, error, refetch };
};
